import { useEffect, useMemo, useState } from "react";
import { fetchAvailability, fetchBookedSlots, createBooking } from "@/lib/api";
import { dayKey, todayISO } from "@/lib/utils";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

function monthDays(year, month) {
  const first = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startPad = first.getDay();
  const cells = [];
  for (let i = 0; i < startPad; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  return cells;
}

export default function CalendarWindow() {
  const now = new Date();
  const [avail, setAvail] = useState(null);
  const [viewYear, setViewYear] = useState(now.getFullYear());
  const [viewMonth, setViewMonth] = useState(now.getMonth());
  const [selectedDate, setSelectedDate] = useState("");
  const [booked, setBooked] = useState([]);
  const [selectedTime, setSelectedTime] = useState("");
  const [form, setForm] = useState({ name: "", email: "", topic: "" });
  const [done, setDone] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchAvailability().then(setAvail).catch(() => toast.error("Could not load availability"));
  }, []);

  useEffect(() => {
    if (!selectedDate) return;
    fetchBookedSlots(selectedDate).then((r) => setBooked(r.booked)).catch(() => {});
  }, [selectedDate]);

  const cells = useMemo(() => monthDays(viewYear, viewMonth), [viewYear, viewMonth]);
  const monthLabel = new Date(viewYear, viewMonth).toLocaleDateString(undefined, { month: "long", year: "numeric" });

  const slots = useMemo(() => {
    if (!avail || !selectedDate) return [];
    const all = avail.weekly_hours[dayKey(selectedDate)] || [];
    if (avail.blocked_dates.includes(selectedDate)) return [];
    return all;
  }, [avail, selectedDate]);

  const isoFor = (d) =>
    `${viewYear}-${String(viewMonth + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

  const prevMonth = () => {
    const d = new Date(viewYear, viewMonth - 1, 1);
    setViewYear(d.getFullYear()); setViewMonth(d.getMonth());
  };
  const nextMonth = () => {
    const d = new Date(viewYear, viewMonth + 1, 1);
    setViewYear(d.getFullYear()); setViewMonth(d.getMonth());
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await createBooking({ date: selectedDate, time: selectedTime, ...form });
      setDone(r.booking);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Booking failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-3 p-6 text-center mac-scroll overflow-auto">
        <CheckCircle2 size={48} className="text-green-500" />
        <div className="text-lg font-semibold" data-testid="booking-success-title">You're booked</div>
        <div className="text-sm text-gray-600">
          {done.date} at {done.time} — a confirmation email is on its way to {done.email}.
        </div>
        <div className="text-xs text-gray-500">Cory will follow up with details.</div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col md:flex-row mac-scroll overflow-auto" data-testid="calendar-window-content">
      <div className="md:w-[52%] p-4 border-b md:border-b-0 md:border-r border-black/10">
        <div className="flex items-center justify-between mb-2">
          <button data-testid="cal-prev-month" onClick={prevMonth} className="px-2 py-1 rounded hover:bg-black/5">&larr;</button>
          <div className="font-semibold text-sm">{monthLabel}</div>
          <button data-testid="cal-next-month" onClick={nextMonth} className="px-2 py-1 rounded hover:bg-black/5">&rarr;</button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center text-[11px] text-gray-500 mb-1">
          {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => <div key={i}>{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((d, i) => {
            if (d == null) return <div key={`e${i}`} />;
            const iso = isoFor(d);
            const isPast = iso < todayISO();
            const isSel = iso === selectedDate;
            return (
              <button
                key={iso}
                data-testid={`cal-day-${iso}`}
                disabled={isPast}
                onClick={() => { setSelectedDate(iso); setSelectedTime(""); }}
                className={`aspect-square rounded-lg text-[13px] transition
                  ${isPast ? "text-gray-300 cursor-not-allowed" : "hover:bg-sky-100"}
                  ${isSel ? "bg-sky-500 text-white font-semibold hover:bg-sky-500" : ""}`}
              >
                {d}
              </button>
            );
          })}
        </div>
      </div>
      <div className="flex-1 p-4">
        {!selectedDate && (
          <div className="h-full flex items-center justify-center text-sm text-gray-500 text-center px-6">
            Pick a date to see open times with Cory.
          </div>
        )}
        {selectedDate && !selectedTime && (
          <div>
            <div className="text-sm font-semibold mb-2">Open times on {selectedDate}</div>
            {slots.length === 0 && <div className="text-sm text-gray-500">No open times this day.</div>}
            <div className="grid grid-cols-3 gap-2">
              {slots.map((t) => {
                const isBooked = booked.includes(t);
                return (
                  <button
                    key={t}
                    data-testid={`slot-${t.replace(":", "")}`}
                    disabled={isBooked}
                    onClick={() => setSelectedTime(t)}
                    className={`px-2 py-2 rounded-lg text-[13px] border transition
                      ${isBooked ? "bg-gray-100 text-gray-400 line-through cursor-not-allowed" : "border-sky-300 text-sky-700 hover:bg-sky-50"}`}
                  >
                    {t}
                  </button>
                );
              })}
            </div>
            <div className="mt-3 text-[11px] text-gray-400">Times shown in Cory's timezone ({avail?.timezone || "America/New_York"}).</div>
          </div>
        )}
        {selectedDate && selectedTime && (
          <form onSubmit={submit} className="flex flex-col gap-3" data-testid="booking-form">
            <div className="text-sm font-semibold">
              {selectedDate} at {selectedTime}{" "}
              <button type="button" className="text-sky-600 text-xs font-normal" onClick={() => setSelectedTime("")}>change</button>
            </div>
            <input data-testid="booking-name" required placeholder="Your name" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="px-3 py-2 rounded-lg border border-black/15 text-base md:text-sm outline-none focus:ring-2 focus:ring-sky-300" />
            <input data-testid="booking-email" required type="email" placeholder="Your email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="px-3 py-2 rounded-lg border border-black/15 text-base md:text-sm outline-none focus:ring-2 focus:ring-sky-300" />
            <textarea data-testid="booking-topic" placeholder="What do you want to talk about?" rows={3} value={form.topic}
              onChange={(e) => setForm({ ...form, topic: e.target.value })}
              className="px-3 py-2 rounded-lg border border-black/15 text-base md:text-sm outline-none focus:ring-2 focus:ring-sky-300 resize-none" />
            <button data-testid="booking-submit" disabled={submitting}
              className="mt-1 px-4 py-2.5 rounded-lg bg-sky-500 text-white text-sm font-semibold hover:bg-sky-600 transition disabled:opacity-50">
              {submitting ? "Booking…" : "Confirm booking"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
